import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
    faBars,
    faCaretDown,
    faCaretRight,
    faCircleExclamation,
    faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty, merge } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import type { Observable } from 'rxjs';
import { useObservableState } from 'observable-hooks';
import { Link } from 'react-router-dom';
import type { Profile } from '../../profile';
import { ProfileProvider, useProfile } from '../../profile';
import { schema } from '../../schema';
import BetaBadge from '../betaBadge/BetaBadge';
import Button from '../form/Button';
import NotificationBadge from '../notificationBadge/NotificationBadge';
import { DEFAULT_SEASONAL_LOGO_RESULT, useSeasonalLogo } from '../seasonal/seasonalLogos';
import {
    accountMenuId,
    downloadsMenuId,
    languageMap,
    languagePlaceholder,
    websiteUrl,
} from './utils';

const listIconClasses = 'xl:ml-1.5 h-3 w-3 fill-current';
const menuItemNameClasses =
    'inline-flex h-20 px-6 xl:px-8 items-center whitespace-nowrap cursor-pointer text-menu-color no-underline justify-between';
const menuItemListClasses = 'inline-flex flex-col group hover:bg-menu-hover xl:border-0';

type MenuContextValue = {
    spaMode: boolean;
    pathname: string;
    search: string;
};

interface SchemaMenuItem {
    id?: string;
    name?: string;
    url?: string;
    beta?: boolean;
    showHelpIcon?: boolean;
    swarmIcon?: string;
    checks?: string[];
    children?: SchemaMenuItem[];
}

interface MenuEntry extends SchemaMenuItem {
    notificationCount: number;
    showIncompleteIcon: boolean;
    isForcedOpen: boolean;
    children: MenuEntry[];
}

type MenuButtonClickHandler = () => void;

interface ButtonElementProps {
    url?: string;
    className?: string;
    id?: string;
    children: ReactNode;
    onClick?: MenuButtonClickHandler;
    'aria-label'?: string;
}

function flipState(isOpen: boolean) {
    return isOpen
        ? // Open for mobile, but hidden for desktop.
          'xl:hidden'
        : // Hidden for mobile, and (somehow?) hidden for desktop.
          'hide';
}

function useTranslatedMenu(): SchemaMenuItem[] {
    const { t } = useTranslation();

    return useMemo(() => {
        const translations = t('MENU', { returnObjects: true });
        return merge([], schema.menu, translations);
    }, [t]);
}

function shouldBeVisible({ checks = [] }: SchemaMenuItem, profile: Profile): boolean {
    const profileRecord = profile;
    return checks.every((check) => Boolean(profileRecord[check as keyof Profile]));
}

function filterMenuForProfile(
    items: SchemaMenuItem[],
    profile: Profile,
    forceOpenMenuId?: string | null
): [MenuEntry[], boolean] {
    const { isProfileIncomplete, readyReportsCount } = profile;
    function showIncompleteIcon(id?: string) {
        return (isProfileIncomplete || readyReportsCount > 0) && id === accountMenuId;
    }
    function showNotificationBadge(id?: string) {
        return readyReportsCount > 0 && id === downloadsMenuId;
    }

    const filteredItems = items
        .filter((item) => shouldBeVisible(item, profile))
        .map((item) => {
            const [children, childForcedOpen] = filterMenuForProfile(
                item.children ?? [],
                profile,
                forceOpenMenuId
            );
            const isForcedOpen =
                childForcedOpen || (forceOpenMenuId ? item.id === forceOpenMenuId : false);

            return {
                ...item,
                showIncompleteIcon: showIncompleteIcon(item.id),
                notificationCount: (showNotificationBadge(item.id) && readyReportsCount) || 0,
                // Replaces any URL containing the languagePlaceholder string with
                // the active language code.
                url: item.url?.replace(languagePlaceholder, profile.languageCode),
                children,
                isForcedOpen,
            } as MenuEntry;
        });

    // Check if any of the items are forced open. If so, this information is passed
    // back to the parent menu item so it can also be marked as forced open.
    const isForcedOpen = filteredItems.some((item) => item.isForcedOpen);

    return [filteredItems, isForcedOpen];
}

function useMenuForProfile(profile: Profile, forceOpenMenuId?: string | null): MenuEntry[] {
    const menu = useTranslatedMenu();

    return useMemo(() => {
        const [items] = filterMenuForProfile(menu, profile, forceOpenMenuId);
        return items;
    }, [menu, profile, forceOpenMenuId]);
}

const MenuContext = createContext<MenuContextValue>({ spaMode: false, pathname: '/', search: '' });

function ButtonElement({ children, url, ...props }: ButtonElementProps): JSX.Element {
    const { spaMode } = useContext(MenuContext);

    if (!url) {
        return (
            <button type="button" {...props}>
                {children}
            </button>
        );
    }

    // In SPA (Single Page Application) mode, typically for environments like Storybook tests,
    // navigation occurs via the React Router `Link` component.
    // This enables client-side routing: the BrowserRouter handles navigation internally,
    // updating the view without requiring a full page reload from the browser.
    if (spaMode) {
        return (
            <Link to={url} {...props}>
                {children}
            </Link>
        );
    }

    // For traditional navigation (e.g., when this menu is used within the Django application),
    // a standard <a> tag is used. This causes the browser to make a full request
    // to the server for the new page.
    return (
        <a href={url} {...props}>
            {children}
        </a>
    );
}
interface SubMenuContainerProps {
    isOpen: boolean;
    isForcedOpen?: boolean;
    children: ReactNode;
}

function SubMenuContainer({ isOpen, isForcedOpen = false, children }: SubMenuContainerProps) {
    return (
        <div className="flex-none">
            <ul
                className={`divide-y xl:absolute xl:w-64 xl:border xl:group-hover:flex xl:group-hover:flex-col ${
                    isForcedOpen ? 'xl:flex xl:flex-col' : flipState(isOpen)
                }`}
            >
                {children}
            </ul>
        </div>
    );
}
interface SubMenuItemProps extends MenuEntry {
    level?: number;
}

function SubMenuItem({
    id,
    name,
    children = [],
    url,
    beta,
    notificationCount,
    level = 1,
    swarmIcon,
    isForcedOpen,
}: SubMenuItemProps) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <li
            className={`${level === 1 && 'group/sub'} hover:bg-menu-hover flex flex-col bg-white`}
            onClick={() => setIsOpen((openState) => !openState)}
        >
            <ButtonElement
                className={`${level === 1 ? 'pl-12' : 'pl-20'} ${menuItemNameClasses} xl:order-2`}
                url={url}
                id={id}
            >
                <span className="flex items-center">
                    {!!swarmIcon && <div className={`${swarmIcon} mr-1.5 inline-block h-6 w-8`} />}
                    {name}
                    {beta && <BetaBadge className="ml-1" />}
                    {!!notificationCount && <NotificationBadge number={notificationCount} />}
                </span>
                {!isEmpty(children) && (
                    <>
                        <FontAwesomeIcon
                            icon={faCaretRight}
                            className={`${listIconClasses} hide xl:block`}
                        />
                        <FontAwesomeIcon
                            icon={faCaretDown}
                            className={`${listIconClasses} block xl:hidden`}
                        />
                    </>
                )}
            </ButtonElement>
            {!isEmpty(children) && (
                <div className="order-2 xl:relative xl:order-1">
                    <ul
                        className={`${
                            isForcedOpen ? 'xl:block' : flipState(isOpen)
                        } divide-y border xl:absolute xl:left-full xl:-mt-px xl:w-full ${
                            level === 1 && 'xl:group-hover/sub:block'
                        }`}
                    >
                        {children.map((item, i) => (
                            <SubMenuItem {...item} key={i} level={level + 1} />
                        ))}
                    </ul>
                </div>
            )}
        </li>
    );
}
interface LanguageMenuItemProps {
    item: Language;
}

function LanguageMenuItem({ item }: LanguageMenuItemProps): JSX.Element {
    const { csrfToken } = useProfile();
    const { pathname, search } = useContext(MenuContext);
    return (
        <form action="/i18n/" method="post" className={item}>
            <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken ?? ''}></input>
            <input name="next" type="hidden" value={`${pathname}${search}`}></input>
            <input name="language" type="hidden" value={item}></input>
            <input
                type="submit"
                value={languageMap[item]}
                className={
                    'text-menu-color h-20 w-full cursor-pointer pl-12 pr-8 text-left xl:w-64 xl:px-8'
                }
            ></input>
        </form>
    );
}
interface LanguageMenuListProps {
    code: Language;
}

function LanguageMenuList({ code }: LanguageMenuListProps): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <button
                className={menuItemNameClasses}
                onClick={() => setIsOpen((openState) => !openState)}
            >
                {languageMap[code]}
                <FontAwesomeIcon icon={faCaretDown} className={listIconClasses} />
            </button>
            <SubMenuContainer isOpen={isOpen}>
                {Object.keys(languageMap).map((item, i) => (
                    <li key={i} className="hover:bg-menu-hover bg-white">
                        <LanguageMenuItem item={item as Language} />
                    </li>
                ))}
            </SubMenuContainer>
        </>
    );
}

function MenuItem({
    id,
    name,
    children = [],
    url,
    showHelpIcon,
    showIncompleteIcon,
    isForcedOpen,
}: MenuEntry) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <ButtonElement
                className={menuItemNameClasses}
                url={url}
                id={id}
                onClick={() => setIsOpen((openState) => !openState)}
            >
                {!showHelpIcon ? (
                    <div>
                        {showIncompleteIcon && (
                            <FontAwesomeIcon
                                icon={faCircleExclamation}
                                className="text-warning h-3 w-3 pb-0.5 pr-1 xl:pb-0"
                                title={gettext('INCOMPLETE_PROFILE')}
                                aria-label={gettext('INCOMPLETE_PROFILE')}
                            />
                        )}
                        {name}
                    </div>
                ) : (
                    <FontAwesomeIcon icon={faQuestionCircle} className="h-4 w-4 fill-current" />
                )}
                {!isEmpty(children) && (
                    <FontAwesomeIcon icon={faCaretDown} className={listIconClasses} />
                )}
            </ButtonElement>
            <SubMenuContainer isOpen={isOpen} isForcedOpen={isForcedOpen}>
                {children.map((item, i) => (
                    <SubMenuItem {...item} key={i} />
                ))}
            </SubMenuContainer>
        </>
    );
}
export interface MenuProps {
    profile: Profile;
    forceMenuOpenById$: Observable<string | null | undefined>;
    spaMode?: boolean;
    location: Pick<Location, 'pathname' | 'search' | 'hash'>;
    replaceLocation: (url: string) => void;
}

export function Menu({
    profile,
    forceMenuOpenById$,
    spaMode = false,
    location,
    replaceLocation,
}: MenuProps) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const { logoutUrlPath, loginUrlPath, signupUrlPath } = schema;
    const {
        isAuthenticated,
        languageCode,
        resellerData: { isActive, topLogoUrl, companyName },
    } = profile;
    const seasonalLogo = useSeasonalLogo() ?? DEFAULT_SEASONAL_LOGO_RESULT;
    const forceOpenMenuId = useObservableState(forceMenuOpenById$);
    const menu = useMenuForProfile(profile, forceOpenMenuId);
    // Accept any login URL variant.
    const isLoginPage = location.pathname === loginUrlPath || location.pathname.includes('login');
    let authTargetUrlPath: string = loginUrlPath;
    let authButtonLabel = t('Log in');

    if (isAuthenticated) {
        authTargetUrlPath = logoutUrlPath;
        authButtonLabel = t('Sign out');
    } else if (isLoginPage) {
        authTargetUrlPath = signupUrlPath;
        authButtonLabel = t('Sign up');
    }
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;

    const handleAuthButton = useCallback(() => {
        if (location.search.includes('next=')) {
            replaceLocation(`${authTargetUrlPath}${location.search}`);
            return;
        }

        const search = new URLSearchParams({ next: redirectPath }).toString();
        replaceLocation(`${authTargetUrlPath}?${search}`);
    }, [authTargetUrlPath, location.search, redirectPath, replaceLocation]);

    return (
        <MenuContext.Provider
            value={{ spaMode, pathname: location.pathname, search: location.search }}
        >
            <ProfileProvider profile={profile}>
                <div className="shadow-menu bg-white">
                    <nav className="border-gray-outline z-nav relative m-auto flex h-20 max-w-screen-2xl flex-wrap items-stretch justify-between px-4">
                        <div className="flex flex-[1_1_10%] items-center">
                            <a className="p-1" href={websiteUrl}>
                                <img
                                    src={isActive && topLogoUrl ? topLogoUrl : seasonalLogo.src}
                                    alt={companyName || seasonalLogo.altText}
                                    className="w-52"
                                />
                            </a>
                        </div>
                        <div className="inline-flex items-center xl:hidden">
                            <button
                                className="hover:bg-menu-hover inline-flex items-center p-2"
                                onClick={() => setIsOpen(!isOpen)}
                            >
                                <FontAwesomeIcon icon={faBars} className="h-8 w-8" />
                            </button>
                        </div>
                        <div
                            className={`${
                                isOpen ? '' : 'hide'
                            } absolute inset-x-0 top-20 block divide-y border-b bg-white text-lg xl:relative xl:top-0 xl:flex xl:items-stretch xl:divide-y-0 xl:border-0 xl:text-xs`}
                        >
                            <ul
                                className={
                                    'box-content flex flex-col items-stretch divide-y xl:flex-grow xl:flex-row xl:divide-y-0'
                                }
                            >
                                {menu.map((item, i) => (
                                    <li className={`${menuItemListClasses}`} key={i}>
                                        <MenuItem {...item} />
                                    </li>
                                ))}
                                <li className={`${menuItemListClasses} language-menu`}>
                                    <LanguageMenuList code={languageCode} />
                                </li>
                            </ul>
                            <div className="flex items-center py-4 pl-4">
                                <Button
                                    variant={isAuthenticated ? 'secondary' : 'primary'}
                                    className="whitespace-nowrap font-bold hover:bg-[#A6A6A6]"
                                    onClick={handleAuthButton}
                                    id="logout"
                                >
                                    {authButtonLabel}
                                </Button>
                            </div>
                        </div>
                    </nav>
                </div>
            </ProfileProvider>
        </MenuContext.Provider>
    );
}
